import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Loader2,
  Bug,
  Lightbulb,
  Heart,
  MessageSquare,
  AlertCircle,
  Send,
  CheckCircle2,
  MoreVertical,
} from "lucide-react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { useAdminFeedback, useUpdateFeedbackStatus, useFeedbackStats, useReplyFeedback } from "@/hooks/use-admin-feedback";
import type { FeedbackType, FeedbackStatus } from "@/integrations/supabase/types";

const TYPE_OPTIONS = [
  { value: "all", label: "Todos os tipos" },
  { value: "bug_report", label: "Bug Report" },
  { value: "feature_request", label: "Sugestao" },
  { value: "compliment", label: "Elogio" },
  { value: "comment", label: "Comentario" },
];

const STATUS_OPTIONS = [
  { value: "all", label: "Todos os status" },
  { value: "new", label: "Novo" },
  { value: "in_review", label: "Em Analise" },
  { value: "in_progress", label: "Em Progresso" },
  { value: "resolved", label: "Resolvido" },
  { value: "closed", label: "Fechado" },
];

const STATUS_UPDATE_OPTIONS: { value: FeedbackStatus; label: string }[] = [
  { value: "new", label: "Novo" },
  { value: "in_review", label: "Em Analise" },
  { value: "in_progress", label: "Em Progresso" },
  { value: "resolved", label: "Resolvido" },
  { value: "closed", label: "Fechado" },
];

const typeBadgeConfig: Record<FeedbackType, { variant: "default" | "secondary" | "destructive" | "outline"; icon: typeof Bug }> = {
  bug_report: { variant: "destructive", icon: Bug },
  feature_request: { variant: "default", icon: Lightbulb },
  compliment: { variant: "secondary", icon: Heart },
  comment: { variant: "outline", icon: MessageSquare },
};

const statusBadgeVariant: Record<FeedbackStatus, "default" | "secondary" | "destructive" | "outline"> = {
  new: "outline",
  in_review: "secondary",
  in_progress: "default",
  resolved: "default",
  closed: "secondary",
};

const statusLabel: Record<FeedbackStatus, string> = {
  new: "Novo",
  in_review: "Em Analise",
  in_progress: "Em Progresso",
  resolved: "Resolvido",
  closed: "Fechado",
};

const typeLabel: Record<FeedbackType, string> = {
  bug_report: "Bug",
  feature_request: "Sugestao",
  compliment: "Elogio",
  comment: "Comentario",
};

export default function AdminFeedback() {
  const [typeFilter, setTypeFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [adminNotes, setAdminNotes] = useState("");
  const [replyText, setReplyText] = useState("");

  const { data, isLoading } = useAdminFeedback({
    type: typeFilter,
    status: statusFilter,
    page,
  });

  const { data: stats, isLoading: statsLoading } = useFeedbackStats();
  const updateStatus = useUpdateFeedbackStatus();
  const replyFeedback = useReplyFeedback();

  const handleOpenDetail = (item: any) => {
    setSelectedItem(item);
    setAdminNotes(item.admin_notes || "");
    setReplyText("");
  };

  const handleStatusChange = (feedbackId: string, newStatus: FeedbackStatus) => {
    updateStatus.mutate({ feedbackId, status: newStatus, adminNotes });
  };

  const handleSaveNotes = () => {
    if (!selectedItem) return;
    updateStatus.mutate({
      feedbackId: selectedItem.id,
      status: selectedItem.status,
      adminNotes,
    });
  };

  const handleSendReply = () => {
    if (!selectedItem || !replyText.trim()) return;
    replyFeedback.mutate(
      { feedbackId: selectedItem.id, reply: replyText },
      { onSuccess: () => setReplyText("") }
    );
  };

  return (
    <div className="p-4 sm:p-6 space-y-6 overflow-x-hidden">
      <div>
        <h2 className="text-xl sm:text-2xl font-bold tracking-tight">Feedback</h2>
        <p className="text-muted-foreground">
          Gerencie o feedback dos usuarios
        </p>
      </div>

      {/* Stats Cards */}
      {!statsLoading && stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-2">
                <MessageSquare className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm text-muted-foreground">Total</span>
              </div>
              <p className="text-2xl font-bold mt-1">{stats.total}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-2">
                <AlertCircle className="h-4 w-4 text-orange-500" />
                <span className="text-sm text-muted-foreground">Em Aberto</span>
              </div>
              <p className="text-2xl font-bold mt-1">{stats.openCount}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-2">
                <Bug className="h-4 w-4 text-red-500" />
                <span className="text-sm text-muted-foreground">Bugs</span>
              </div>
              <p className="text-2xl font-bold mt-1">{stats.byType.bug_report}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-2">
                <Lightbulb className="h-4 w-4 text-amber-500" />
                <span className="text-sm text-muted-foreground">Sugestoes</span>
              </div>
              <p className="text-2xl font-bold mt-1">{stats.byType.feature_request}</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 flex-wrap">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium whitespace-nowrap">Tipo:</span>
              <Select value={typeFilter} onValueChange={(v) => { setTypeFilter(v); setPage(1); }}>
                <SelectTrigger className="w-full sm:w-44">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TYPE_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium whitespace-nowrap">Status:</span>
              <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(1); }}>
                <SelectTrigger className="w-full sm:w-44">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STATUS_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-primary" />
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="hidden sm:table-cell">Data</TableHead>
                      <TableHead className="hidden md:table-cell">Usuario</TableHead>
                      <TableHead>Tipo</TableHead>
                      <TableHead>Titulo</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="hidden lg:table-cell">Pagina</TableHead>
                      <TableHead>Acoes</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(data?.items ?? []).map((item) => {
                      const typeConfig = typeBadgeConfig[item.type];
                      const TypeIcon = typeConfig.icon;

                      return (
                        <TableRow key={item.id} className="cursor-pointer" onClick={() => handleOpenDetail(item)}>
                          <TableCell className="hidden sm:table-cell whitespace-nowrap">
                            {format(new Date(item.created_at), "dd/MM/yyyy HH:mm", { locale: ptBR })}
                          </TableCell>
                          <TableCell className="hidden md:table-cell">
                            <div className="min-w-0">
                              <div className="font-medium text-sm truncate">{item.user_name || "-"}</div>
                              <div className="text-xs text-muted-foreground truncate max-w-[150px]">{item.user_email || "-"}</div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge variant={typeConfig.variant} className="gap-1">
                              <TypeIcon className="h-3 w-3" />
                              <span className="hidden sm:inline">{typeLabel[item.type]}</span>
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <div className="min-w-0">
                              <div className="max-w-[150px] sm:max-w-[200px] truncate font-medium">
                                {item.title}
                              </div>
                              {/* Show user + date on mobile under title */}
                              <div className="md:hidden text-xs text-muted-foreground truncate max-w-[150px] mt-0.5">
                                {item.user_name || item.user_email || "-"}
                              </div>
                              <div className="sm:hidden text-xs text-muted-foreground mt-0.5">
                                {format(new Date(item.created_at), "dd/MM/yy", { locale: ptBR })}
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge variant={statusBadgeVariant[item.status]}>
                              {statusLabel[item.status]}
                            </Badge>
                          </TableCell>
                          <TableCell className="hidden lg:table-cell text-xs text-muted-foreground max-w-[120px] truncate">
                            {item.page_url || "-"}
                          </TableCell>
                          <TableCell>
                            {/* Desktop: inline select */}
                            <div className="hidden sm:block" onClick={(e) => e.stopPropagation()}>
                              <Select
                                value={item.status}
                                onValueChange={(v) => {
                                  handleStatusChange(item.id, v as FeedbackStatus);
                                }}
                              >
                                <SelectTrigger className="w-32 h-8">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {STATUS_UPDATE_OPTIONS.map((opt) => (
                                    <SelectItem key={opt.value} value={opt.value}>
                                      {opt.label}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                            {/* Mobile: dropdown */}
                            <div className="sm:hidden" onClick={(e) => e.stopPropagation()}>
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button variant="ghost" size="icon" className="h-10 w-10">
                                    <MoreVertical className="h-4 w-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  <DropdownMenuItem onClick={() => handleOpenDetail(item)}>
                                    Ver Detalhes
                                  </DropdownMenuItem>
                                  {STATUS_UPDATE_OPTIONS.map((opt) => (
                                    <DropdownMenuItem
                                      key={opt.value}
                                      onClick={() => handleStatusChange(item.id, opt.value)}
                                      className={item.status === opt.value ? "bg-accent" : ""}
                                    >
                                      {opt.label}
                                    </DropdownMenuItem>
                                  ))}
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    {(data?.items ?? []).length === 0 && (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center text-muted-foreground py-8">
                          Nenhum feedback encontrado
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>

              {data && data.total > 20 && (
                <div className="flex justify-center gap-2 mt-4">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page <= 1}
                    onClick={() => setPage((p) => p - 1)}
                  >
                    Anterior
                  </Button>
                  <span className="text-sm text-muted-foreground self-center">
                    Pagina {page} de {Math.ceil(data.total / 20)}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page >= Math.ceil(data.total / 20)}
                    onClick={() => setPage((p) => p + 1)}
                  >
                    Proxima
                  </Button>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Detail Dialog */}
      <Dialog open={!!selectedItem} onOpenChange={(open) => !open && setSelectedItem(null)}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {selectedItem && (
                <>
                  {(() => {
                    const Icon = typeBadgeConfig[selectedItem.type as FeedbackType].icon;
                    return <Icon className="h-5 w-5 shrink-0" />;
                  })()}
                  <span className="truncate">{selectedItem?.title}</span>
                </>
              )}
            </DialogTitle>
          </DialogHeader>
          {selectedItem && (
            <div className="space-y-4">
              <div className="flex items-center gap-2 flex-wrap">
                <Badge variant={typeBadgeConfig[selectedItem.type as FeedbackType].variant}>
                  {typeLabel[selectedItem.type as FeedbackType]}
                </Badge>
                <Badge variant={statusBadgeVariant[selectedItem.status as FeedbackStatus]}>
                  {statusLabel[selectedItem.status as FeedbackStatus]}
                </Badge>
                <span className="text-xs text-muted-foreground">
                  {format(new Date(selectedItem.created_at), "dd/MM/yyyy 'as' HH:mm", { locale: ptBR })}
                </span>
              </div>

              <div>
                <span className="text-sm font-medium">Usuario:</span>
                <p className="text-sm text-muted-foreground break-all">
                  {selectedItem.user_name || "-"} ({selectedItem.user_email || "-"})
                </p>
              </div>

              {selectedItem.description && (
                <div>
                  <span className="text-sm font-medium">Descricao:</span>
                  <p className="text-sm text-muted-foreground whitespace-pre-wrap mt-1">
                    {selectedItem.description}
                  </p>
                </div>
              )}

              {selectedItem.page_url && (
                <div>
                  <span className="text-sm font-medium">Pagina:</span>
                  <p className="text-sm text-muted-foreground break-all">{selectedItem.page_url}</p>
                </div>
              )}

              {selectedItem.user_agent && (
                <div>
                  <span className="text-sm font-medium">User Agent:</span>
                  <p className="text-xs text-muted-foreground break-all">{selectedItem.user_agent}</p>
                </div>
              )}

              <div className="space-y-2">
                <Label>Status</Label>
                <Select
                  value={selectedItem.status}
                  onValueChange={(v) => handleStatusChange(selectedItem.id, v as FeedbackStatus)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {STATUS_UPDATE_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Notas internas</Label>
                <Textarea
                  value={adminNotes}
                  onChange={(e) => setAdminNotes(e.target.value)}
                  placeholder="Notas visiveis apenas para o admin..."
                  rows={2}
                />
                <Button size="sm" variant="outline" onClick={handleSaveNotes} disabled={updateStatus.isPending}>
                  {updateStatus.isPending && <Loader2 className="h-3 w-3 mr-2 animate-spin" />}
                  Salvar Notas
                </Button>
              </div>

              <div className="space-y-2 border-t pt-4">
                <Label className="flex items-center gap-2">
                  <Send className="h-4 w-4" />
                  Responder ao usuario por email
                </Label>
                {selectedItem.replied_at && (
                  <div className="flex items-center gap-2 text-xs text-muted-foreground bg-muted px-3 py-2 rounded-md">
                    <CheckCircle2 className="h-3.5 w-3.5 text-green-500 shrink-0" />
                    <span className="break-all">
                      Respondido em {format(new Date(selectedItem.replied_at), "dd/MM/yyyy 'as' HH:mm", { locale: ptBR })}
                    </span>
                  </div>
                )}
                {selectedItem.admin_reply && (
                  <div className="bg-muted/50 border rounded-md px-3 py-2">
                    <p className="text-xs font-medium text-muted-foreground mb-1">Ultima resposta enviada:</p>
                    <p className="text-sm whitespace-pre-wrap">{selectedItem.admin_reply}</p>
                  </div>
                )}
                <Textarea
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                  placeholder={`Escreva uma resposta para ${selectedItem.user_email || "o usuario"}...`}
                  rows={3}
                />
                <Button
                  size="sm"
                  onClick={handleSendReply}
                  disabled={replyFeedback.isPending || !replyText.trim()}
                  className="w-full sm:w-auto"
                >
                  {replyFeedback.isPending
                    ? <Loader2 className="h-3 w-3 mr-2 animate-spin" />
                    : <Send className="h-3 w-3 mr-2" />
                  }
                  Enviar por Email
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
